import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import {
  dismissAnnouncement as dismissApi,
  listAnnouncementsForUser,
  listMyReads,
  markAnnouncementRead as markReadApi,
  type AdminAnnouncement,
  type AnnouncementRead,
} from "@/lib/announcements";

type State = {
  announcements: AdminAnnouncement[];
  reads: AnnouncementRead[];
  loading: boolean;
};

export const useAnnouncements = () => {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [state, setState] = useState<State>({ announcements: [], reads: [], loading: true });

  const refresh = useCallback(async () => {
    if (!userId) {
      setState({ announcements: [], reads: [], loading: false });
      return;
    }
    try {
      const [announcements, reads] = await Promise.all([listAnnouncementsForUser(), listMyReads()]);
      setState({ announcements, reads, loading: false });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [userId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!userId) return;
    const uniqueId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2);
    const channelName = `admin_announcements_user_${userId}_${uniqueId}`;
    const channel = supabase
      .channel(channelName)
      .on("postgres_changes", { event: "*", schema: "public", table: "admin_announcements" }, () => {
        void refresh();
      })
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, refresh]);

  const readMap = useMemo(() => {
    const m = new Map<string, AnnouncementRead>();
    for (const r of state.reads) m.set(r.announcement_id, r);
    return m;
  }, [state.reads]);

  const visible = useMemo(() => {
    return state.announcements.filter((a) => {
      const r = readMap.get(a.id);
      return !r?.dismissed_at;
    });
  }, [state.announcements, readMap]);

  const unread = useMemo(() => visible.filter((a) => !readMap.get(a.id)), [visible, readMap]);

  const markRead = useCallback(
    async (id: string) => {
      if (!user) return;
      await markReadApi(id, user.id);
      await refresh();
    },
    [user, refresh],
  );

  const dismiss = useCallback(
    async (id: string) => {
      if (!user) return;
      await dismissApi(id, user.id);
      await refresh();
    },
    [user, refresh],
  );

  return {
    announcements: state.announcements,
    visible,
    unread,
    reads: state.reads,
    loading: state.loading,
    isRead: (id: string) => Boolean(readMap.get(id)),
    markRead,
    dismiss,
    refresh,
  };
};
